import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import currencies from "../../Currency/currency.json";

const apiUrl = process.env.REACT_APP_API_URL;

const getStoredUser = () => {
  const stored = localStorage.getItem(process.env.REACT_APP_USERKEY);
  if (!stored || stored === "undefined") return null;
  return JSON.parse(stored);
};

export const loginUser = createAsyncThunk(
  "user/loginUser",
  async ({ email, password }, { dispatch, rejectWithValue }) => {
    try {
      const res = await axios.post(`${apiUrl}/user/login`, { email, password });
      if (res.status === 200 && res.data.is_enabled) {
        localStorage.setItem(
          process.env.REACT_APP_USERKEY,
          JSON.stringify(res.data)
        );
        dispatch(fetchUpdatedBankingInfo(email));
        return res.data;
      } else if (!res.data.is_enabled) {
        return rejectWithValue("Your account is not enabled yet!");
      } else {
        return rejectWithValue("Email and password do not match!");
      }
    } catch (err) {
      return rejectWithValue("Login failed, please try again!");
    }
  }
);

export const fetchUpdatedBankingInfo = createAsyncThunk(
  "user/fetchBankingInfo",
  async (email, { rejectWithValue }) => {
    try {
      const [cardRes, accRes] = await Promise.all([
        axios.get(`${apiUrl}/payment/getCards?email=${email}`),
        axios.get(`${apiUrl}/payment/bankInfo?email=${email}`),
      ]);

      let from = [],
        to = [];

      cardRes.data?.forEach((card) => {
        const c = currencies.find((cur) => cur.countryCode === card.country);
        if (c && !from.includes(c.code)) from.push(c.code);
      });

      accRes.data?.bankAccounts?.forEach((acc) => {
        if (!to.includes(acc.currency)) to.push(acc.currency.toUpperCase());
      });

      const updatedUser = {
        ...getStoredUser(),
        currency: { from, to },
        bankingInfo: {
          paymentCards: cardRes.data || [],
          depositAccounts: accRes.data.bankAccounts || [],
        },
      };

      localStorage.setItem(
        process.env.REACT_APP_USERKEY,
        JSON.stringify(updatedUser)
      );
      return updatedUser;
    } catch (err) {
      return rejectWithValue("Failed to fetch updated banking info");
    }
  }
);

export const fetchOrderHistory = createAsyncThunk(
  "user/fetchOrderHistory",
  async (userId, { rejectWithValue }) => {
    try {
      const res = await axios.get(
        `${apiUrl}/transaction/getAllTransactionsByUserId?userId=${userId}`
      );
      return res.data || [];
    } catch (err) {
      return rejectWithValue("Failed to fetch order history.");
    }
  }
);

const userSlice = createSlice({
  name: "user",
  initialState: {
    loading: false,
    user: { login: false },
    error: null,
    orderHistory: [],
  },
  reducers: {
    logout: (state) => {
      state.user = { login: false };
      state.error = null;
      localStorage.removeItem(process.env.REACT_APP_USERKEY);
    },
    initializeUserFromStorage: (state) => {
      const stored = getStoredUser();
      if (stored) {
        state.user = { login: true, data: stored };
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false;
        state.user = { login: true, data: action.payload };
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(fetchUpdatedBankingInfo.fulfilled, (state, action) => {
        state.user.data = action.payload;
      })
      .addCase(fetchUpdatedBankingInfo.rejected, (state, action) => {
        state.error = action.payload;
      })
      .addCase(fetchOrderHistory.fulfilled, (state, action) => {
        state.orderHistory = action.payload;
      })
      .addCase(fetchOrderHistory.rejected, (state, action) => {
        state.error = action.payload;
      });
  },
});

export const { logout, initializeUserFromStorage } = userSlice.actions;
export default userSlice.reducer;
